import { useAuth0 } from "@auth0/auth0-react";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useContextState } from "../context/ContextProvider";
import Archive from "../pages/Archive";
import AuditLog from "../pages/AuditLog";
import IntuitConnect from "../pages/IntuitConnect";
import IntuitConnectComplete from "../pages/IntuitConnectComplete";
import IntuitDisconnect from "../pages/IntuitDisconnect";
import IntuitDisconnectComplete from "../pages/IntuitDisconnectComplete";
import IntuitLaunch from "../pages/IntuitLaunch";
import IntuitUnverified from "../pages/IntuitUnverified";
import Login from "../pages/Login";
import Logout from "../pages/Logout";
import NotFound from "../pages/NotFound";
import Organization from "../pages/Organization";
import PaymentMethods from "../pages/PaymentMethods";
import Settings from "../pages/Settings";
import Billing from "../pages/Settings/Billing";
import MyProfile from "../pages/Settings/MyProfile";
import SettingsNotifications from "../pages/Settings/SettingsNotifications";
import Users from "../pages/Users";
import Resellers from "../pages/Resellers";
import CompleteProfile from "../pages/CompleteProfile";
import ResellerInvite from "../pages/ResellerInvite";
import ResellerInviteConnected from "../pages/ResellerInvite/Connected";
import ResellerDirectConnect from "../pages/ResellerDirectConnect";
import MonthlyReport from "../pages/MonthlyReport";
import OrgMonthlyReport from "../pages/MonthlyReport/OrgMonthlyReport";
import { Layout } from "../shared/Layout/Layout";
import ProtectedRoute from "./ProtectedRoutes";
import ResellerUsers from "../pages/Resellers/ResellerUsers";
import RestoreReport from "../pages/Organization/RestoreReport";
import {
  consumePostOnboardingProfileRedirect,
  getAuthenticatedLandingPath,
} from "../utils/postOnboardingRedirect";
import {
  isIntuitConnection,
  isIntuitUnverified,
} from "../utils/auth";

const AppRoutes = () => {
  const { collapseSideBar } = useContextState();
  const location = useLocation();
  const emailVerified = useSelector((state) => state.commonReducer.emailVerified);
  const profileCompleted = useSelector(
    (state) => state.commonReducer.profileCompleted
  );
  const userData = useSelector((state) => state.commonReducer.userData);
  const { isAuthenticated, isLoading: auth0Loading, user } = useAuth0();
  const navigate = useNavigate();

  const isCompleteProfilePage = location.pathname.startsWith("/complete-profile");
  const isIntuitUnverifiedPage = location.pathname.startsWith("/intuit-unverified");

  // After Complete Profile, land on My Profile even if Auth0 returns to `/`.
  useEffect(() => {
    if (!isAuthenticated || auth0Loading) return;
    if (
      consumePostOnboardingProfileRedirect() &&
      !location.pathname.startsWith("/settings/profile")
    ) {
      navigate("/settings/profile", { replace: true });
    }
  }, [isAuthenticated, auth0Loading, location.pathname, navigate]);

  // Handle server-side 404s on deep links by catching an internal_redirect param on the root
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const internalRedirect = params.get("internal_redirect");
    if (internalRedirect) {
      params.delete("internal_redirect");
      const remainingQs = params.toString();
      navigate(`${internalRedirect}${remainingQs ? "?" + remainingQs : ""}`, { replace: true });
    }
  }, [location.search, navigate]);
  const sidebarMargin = isCompleteProfilePage || isIntuitUnverifiedPage
    ? ""
    : collapseSideBar
      ? "sm:ml-20"
      : "sm:ml-64";

  // Check if current user logged in with an unverified Intuit account
  const isIntuitUser = isIntuitConnection(user?.sub);
  const isUnverifiedIntuit = isIntuitUnverified(null, user);

  // Intuit App Store entry URLs must complete SSO / OAuth before any other
  // onboarding gate (complete-profile) interrupts the flow Intuit reviews.
  const isIntuitAppStoreFlow = [
    "/intuit-launch",
    "/intuit-connect",
    "/intuit-connect-complete",
    "/intuit-disconnect",
    "/intuit-disconnect-complete",
    "/intuit-unverified",
  ].some((path) => location.pathname.startsWith(path));

  // If user is an unverified Intuit account, immediately block and display notice
  if (isAuthenticated && isUnverifiedIntuit && !isIntuitUnverifiedPage) {
    return <Navigate to="/intuit-unverified" replace />;
  }

  // Intuit users bypass complete-profile per Intuit SSO provisioning requirement
  const needsCompleteProfile =
    !isIntuitUser &&
    (profileCompleted === false ||
      (profileCompleted === null && emailVerified === false));

  // Complete Profile (email OTP + phone + company) — only while profile is incomplete
  if (
    isAuthenticated &&
    needsCompleteProfile &&
    !isCompleteProfilePage &&
    !isIntuitAppStoreFlow
  ) {
    const params = new URLSearchParams();
    const profileEmail = user?.email || userData?.email || "";
    const profileSub = user?.sub || "";
    if (profileEmail) params.set("email", profileEmail);
    if (profileSub) params.set("sub", profileSub);
    const auth0State = new URLSearchParams(location.search).get("state");
    if (auth0State) params.set("state", auth0State);
    const qs = params.toString();
    return (
      <Navigate to={qs ? `/complete-profile?${qs}` : "/complete-profile"} replace />
    );
  }

  return (
    <div className={sidebarMargin}>
    <Routes>
        {/* Public routes */}
        <Route
          path={"/login"}
          element={
            isAuthenticated ? (
              <Navigate to={getAuthenticatedLandingPath()} replace />
            ) : (
              <Login />
            )
          }
        />
        <Route path="/complete-profile" element={<CompleteProfile />} />
        <Route path="/intuit-unverified" element={<IntuitUnverified />} />
        <Route path="/reseller-invite/:referralCode" element={<ResellerInvite />} />
        <Route path="/reseller-invite/connected" element={<ResellerInviteConnected />} />
        <Route path="/connect/xero/:referralCode" element={<ResellerDirectConnect platform="xero" />} />
        <Route path="/connect/qbo/:referralCode" element={<ResellerDirectConnect platform="qbo" />} />

        {/* Intuit App Store / QBO Apps tab Launch, Connect, and Disconnect URLs.
            Must work with no prior Wowzer session (Intuit sends users here
            directly), so these stay outside ProtectedRoute. */}
        <Route path="/intuit-launch" element={<IntuitLaunch />} />
        <Route path="/intuit-connect" element={<IntuitConnect />} />
        <Route path="/intuit-connect-complete" element={<IntuitConnectComplete />} />
        <Route path="/intuit-disconnect" element={<IntuitDisconnect />} />
        <Route path="/intuit-disconnect-complete" element={<IntuitDisconnectComplete />} />

        {/* Protected routes - all wrapped in Layout */}
        <Route element={<ProtectedRoute />}>
          <Route
            path={"/"}
            element={<Navigate to={getAuthenticatedLandingPath()} replace />}
          />
          <Route
            path={"/organizations"}
            element={
              <Layout>
                <Organization />
              </Layout>
            }
          />
          <Route
            path={"/qbo-organizations"}
            element={<Navigate to="/organizations" replace />}
          />
          <Route
            path={"/users"}
            element={
              <Layout>
                <Users />
              </Layout>
            }
          />
          <Route
            path={"/resellers"}
            element={
              <Layout>
                <Resellers />
              </Layout>
            }
          />

          <Route 
            path={"/reseller/customers"} 
            element={<Layout><ResellerUsers /></Layout>} 
          />

          <Route 
            path={"/resellers/users/:resellerId"} 
            element={<Layout><ResellerUsers /></Layout>} 
          />


          <Route
            path="/restore-report/:type/:restoreQueueId"
            element={
              <Layout>
                <RestoreReport />
              </Layout>
            }
          />
    
          <Route
            path={"/archive"}
            element={
              <Layout>
                <Archive />
              </Layout>
            }
          />
          <Route
            path={"/archive/:orgId"}
            element={
              <Layout>
                <Archive />
              </Layout>
            }
          />
          <Route
            path={"/archive/:orgId/:dateFolder"}
            element={
              <Layout>
                <Archive />
              </Layout>
            }
          />

          {/* Settings routes with nested children */}
          <Route
            path="/settings"
            element={
              <Layout>
                <Settings />
              </Layout>
            }
          >
            <Route index element={<Navigate to="profile" replace />} />
            <Route path="profile" element={<MyProfile />} />
            <Route path="billing" element={<Billing />} />
            <Route path="notifications" element={<SettingsNotifications />} />
          </Route>

          <Route
            path={"/audit-log"}
            element={
              <Layout>
                <AuditLog />
              </Layout>
            }
          />
          <Route
            path={"/home"}
            element={
              <Layout>
                <MonthlyReport />
              </Layout>
            }
          />
          <Route
            path={"/monthly-report"}
            element={
              <Layout>
                <MonthlyReport />
              </Layout>
            }
          />
          <Route
            path={"/monthly-report/:platform/:orgId"}
            element={
              <Layout>
                <OrgMonthlyReport />
              </Layout>
            }
          />
          <Route
            path={"/monthly-report/org/:orgId"}
            element={
              <Layout>
                <OrgMonthlyReport />
              </Layout>
            }
          />

          <Route
            path={"/payment-methods"}
            element={
              <Layout>
                <PaymentMethods />
              </Layout>
            }
          />
          <Route
            path={"/logout"}
            element={
              <Layout>
                <Logout />
              </Layout>
            }
          />
        </Route>

        {/* Catch all route - redirects to login if not authenticated */}
        <Route
          path="*"
          element={
            isAuthenticated ? <NotFound /> : <Navigate to="/login" replace />
          }
        />
    </Routes>
    </div>
  );
};

export default AppRoutes;
